import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  ApiException,
  ApplySessionPricesSuccessResponseDto,
  BasketClient,
  EventPlanManagementBlockDetailsClient,
  EventPlanManagementClient,
  EventPlanManagementDetailsClient,
  EventSessionPlanBlockViewDetailsResponseDto,
  EventSessionPlanContingentImageResponseDto,
  EventSessionPlanFilterRequestDto,
  EventSessionPlanImageResponseDto,
  EventSessionPlanLetterResponseDto,
  EventSessionPlanNumberedBlockFilterResponseDto,
  EventSessionPlanResponseDto,
  EventSessionPlanShapeResponseDto,
  EventSessionSaleWithPlanClient,
  EventSessionSalesPlanSeatDetailsResponseDto,
  EventSessionSalesSeatClient,
  EventSessionSalesWithPlanFilterClient,
  EventSessionSalesWithPlanLegendClient,
  FileResponse,
  OperationResultOfBasketSessionSeatStatusResponseDto,
  OrderSeatClient,
  SalesPlanBlockDetailsResponseDto,
  SalesPlanBlockFilterRequestDto,
  SalesPlanBlockFilterResponseDto,
  SalesPlanBlockResponseDto,
  SalesPlanFiltersCategoryResponseDto,
  SalesPlanFiltersContingentResponseDto,
  SalesPlanFiltersPriceResponseDto,
  GetEventSessionSalesPlanLegendResponse,
  GetEventSessionSalesPlanFilterPricesResponse,
  GetEventSessionSalesPlanFilterCategoriesResponse,
  GetEventSessionSalesPlanFilterContingentsResponse,
  ApplyFilterSalesPlanBlockResponse,
  GetEventSessionSalesPlanSeatsResponse,
  GetEventSessionSalesPlanSeatDetailsResponse,
  GetSalesPlanBlocksResponse,
  GetEventSessionSalesPlanBlockDetailsResponse,
  SalesPlanLegendResponseDto,
  SalesWithPlanBlockClient,
  SeatPriceApplyingRequestDto,
  SeatsBlockGroupResponseDto,
  SessionSeatsRequestDto,
  StampedVersionSeatsResponseDto,
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import { Store, select } from '@ngrx/store';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { SalesEventTypeEnum } from '@sales/models/sales.model';
import { catchError, map, Observable, of, switchMap, take, throwError } from 'rxjs';
import { SalesPlanAbstractService } from './sales-plan-abstract.service';

/**
 * Le 204 du fond de plan est une reponse normale, pas un echec : le client genere en fait
 * pourtant une exception. C'est le cas des operations qui renvoient un fichier — leur 200 porte
 * un `FileResponse` et leur 204 n'a aucun type, que le generateur traite en erreur. Les commandes
 * dont le 204 *est* le succes, elles, le rendent normalement.
 */
function isNoContent(error: unknown): boolean {
  return !!error && ApiException.isApiException(error) && error.status === HttpStatusCode.NoContent;
}

@Injectable({ providedIn: 'root' })
export class SalesPlanEventService implements SalesPlanAbstractService {
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly basketClient = inject(BasketClient);
  private readonly eventSessionSaleWithPlanClient = inject(EventSessionSaleWithPlanClient);
  private readonly orderSeatClient = inject(OrderSeatClient);
  private readonly eventSessionSalesWithPlanLegendClient = inject(EventSessionSalesWithPlanLegendClient);
  private readonly salesWithPlanBlockClient = inject(SalesWithPlanBlockClient);
  private readonly filterClient = inject(EventSessionSalesWithPlanFilterClient);
  private readonly seatsClient = inject(EventSessionSalesSeatClient);
  private readonly eventPlanManagementBlockDetailsClient = inject(EventPlanManagementBlockDetailsClient);
  private readonly eventPlanManagementDetails = inject(EventPlanManagementDetailsClient);
  private readonly eventPlanManagementClient = inject(EventPlanManagementClient);
  private readonly httpHandlerService = inject(HttpHandlerService);

  _type: SalesEventTypeEnum = SalesEventTypeEnum.Event;

  /**
   * Le fond de plan est optionnel : une seance sans image repond 204, et l'appelant doit recevoir
   * un fond nul, pas une erreur. Sans ce repli, l'exception du 204 fait echouer le `forkJoin` du
   * chargement du topview et masque l'erreur metier des blocs — dont le message de diagnostic
   * n'atteint alors jamais l'utilisateur.
   */
  public getPlanBackground(eventSessionId: number): Observable<FileResponse | null> {
    return this.eventPlanManagementClient
      .getCatalogEventBackGroundFile(eventSessionId, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<FileResponse>(),
        // Apres `handleApiResponse`, pas avant : son `filter` ecarterait un fond nul emis en
        // amont, et une source qui complete sans rien emettre fait completer tout le `forkJoin`
        // a vide — soit le meme symptome par un autre chemin.
        catchError((error: unknown) => (isNoContent(error) ? of(null) : throwError(() => error))),
      );
  }

  public getTopviewPlanBlocks(
    sessionId: number,
    blockId: number,
    filters: EventSessionPlanFilterRequestDto = {},
  ): Observable<EventSessionPlanResponseDto> {
    return this.eventSessionSaleWithPlanClient
      .getEventSessionPlanBlocks(sessionId, blockId, filters, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanResponseDto>());
  }

  getTopviewPlanOnlyBlocks(
    eventsessionId: number,
    channelId: number,
  ): Observable<SalesPlanBlockResponseDto[]> {
    return this.salesWithPlanBlockClient.getEventSessionSalesPlanBlocks(eventsessionId, channelId, Constants.ApiVersion).pipe(
      this.httpHandlerService.handleApiResponse<GetSalesPlanBlocksResponse>(),
      map((response) => response.blocks ?? []),
    );
  }

  public getBlockData(
    sessionId: number,
    blockId: number,
    filters: EventSessionPlanFilterRequestDto = {},
  ): Observable<EventSessionPlanBlockViewDetailsResponseDto> {
    return this.eventSessionSaleWithPlanClient
      .getEventSessionPlanBlockViewDetails(sessionId, blockId, filters, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanBlockViewDetailsResponseDto>());
  }

  public getNumberedPlanFilter(
    sessionId: number,
    filter: EventSessionPlanFilterRequestDto,
  ): Observable<EventSessionPlanNumberedBlockFilterResponseDto> {
    return this.eventSessionSaleWithPlanClient
      .getNumberedPlanFilter(sessionId, filter, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanNumberedBlockFilterResponseDto>());
  }

  public getTootltipSeatInfo(
    sessionId: number,
    seatId: number,
    channelId: number,
  ): Observable<EventSessionSalesPlanSeatDetailsResponseDto> {
    return this.seatsClient
      .getEventSessionSalesPlanSeatDetails(channelId, sessionId, seatId, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanSeatDetailsResponse>(),
        map((response) => response.seatDetails!),
      );
  }

  public getBlockDetails(
    channelId: number,
    eventSessionId: number,
    blockId: number,
  ): Observable<SalesPlanBlockDetailsResponseDto> {
    return this.salesWithPlanBlockClient.getEventSessionSalesPlanBlockDetails(channelId, eventSessionId, blockId, Constants.ApiVersion).pipe(
      this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanBlockDetailsResponse>(),
      map((response) => response.blockDetails!),
    );
  }

  public flagSeats(
    seatIds: number[],
    sessionId: number,
    basketId: number,
  ): Observable<OperationResultOfBasketSessionSeatStatusResponseDto> {
    return this.basketClient
      .changeSeatBookingStatus(
        {
          basketId: basketId ?? -1,
          sessionId,
          seatIds,
        },
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<OperationResultOfBasketSessionSeatStatusResponseDto>());
  }

  public flagUnnumberedSeats(
    sessionId: number,
    basketId: number,
    sessionSeats: SessionSeatsRequestDto[],
  ): Observable<OperationResultOfBasketSessionSeatStatusResponseDto> {
    return this.basketClient
      .bookUnnumbered(
        {
          id:  -1,
          basketId: basketId ?? -1,
          sessionId,
          isContiguous: true,
          sessionSeats,
        },
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<OperationResultOfBasketSessionSeatStatusResponseDto>());
  }

  public applyPrices(
    basketId: number,
    sessionId: number,
    seatPriceApplyings: SeatPriceApplyingRequestDto[],
  ): Observable<ApplySessionPricesSuccessResponseDto> {
    return this.basketClient
      .applyPrices(basketId, sessionId, seatPriceApplyings, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<ApplySessionPricesSuccessResponseDto>());
  }

  public getSearchResults(
    sessionId: number,
    blockId: number,
    searchTerms: string,
  ): Observable<SeatsBlockGroupResponseDto[]> {
    return this.orderSeatClient
      .getAllByFilter(sessionId, blockId, searchTerms, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SeatsBlockGroupResponseDto[]>());
  }

  public cancelBasket() {
    return this.store.pipe(
      take(1),
      select(fromSalesPlan.selectors.selectBasketId),
      switchMap((basketId) =>
        basketId > 0
          ? this.basketClient
              .cancel(basketId, Constants.ApiVersion)
              .pipe(this.httpHandlerService.handleApiResponse<boolean>())
          : of(false),
      ),
    );
  }
  public getLengendInformation(
    channelId: number,
    eventSessionId: number,
  ): Observable<SalesPlanLegendResponseDto> {
    return this.eventSessionSalesWithPlanLegendClient
      .getEventSessionPlanLegend(channelId, eventSessionId, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanLegendResponse>(),
        map((response) => response.legend!),
      );
  }

  public getPricesList(
    eventSessionId: number,
    channelId: number,
    contingentIds: number[],
    categoryIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersPriceResponseDto[]> {
    return this.filterClient
      .getEventSessionSalesPlanPrices(
        eventSessionId,
        channelId,
        contingentIds,
        categoryIds,
        blockId,
        Constants.ApiVersion,
      )
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanFilterPricesResponse>(),
        map((response) => response.prices ?? []),
      );
  }

  getContingentsList(
    eventSessionId: number,
    channelId: number,
    categoryIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersContingentResponseDto[]> {
    return this.filterClient
      .getEventSessionSalesPlanContingents(
        channelId,
        eventSessionId,
        categoryIds,
        priceIds,
        blockId,
        Constants.ApiVersion,
      )
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanFilterContingentsResponse>(),
        map((response) => response.contingents ?? []),
      );
  }

  getCategoriesList(
    eventSessionId: number,
    channelId: number,
    contingentIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersCategoryResponseDto[]> {
    return this.filterClient
      .getEventSessionSalesPlanCategories(
        eventSessionId,
        channelId,
        priceIds,
        contingentIds,
        blockId,
        Constants.ApiVersion,
      )
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanFilterCategoriesResponse>(),
        map((response) => response.categories ?? []),
      );
  }

  applyTopviewFilter(
    channelId: number,
    eventSessionId: number,
    filters: SalesPlanBlockFilterRequestDto,
  ): Observable<SalesPlanBlockFilterResponseDto[]> {
    return this.filterClient
      .eventSessionFilterPlanBlocksApplyFilter(channelId, eventSessionId, filters, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<ApplyFilterSalesPlanBlockResponse>(),
        map((response) => response.blocks ?? []),
      );
  }

  getBlockViewSeats(
    salesChannelId: number,
    eventSessionId: number,
    blockId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this.seatsClient
      .getSalesPlanSeatsFromBlock(salesChannelId, eventSessionId, blockId, null, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanSeatsResponse>(),
        map((response) => response.seats!),
      );
  }

  getDifferentialBlockViewSeats(
    salesChannelId: number,
    eventSessionId: number,
    blockId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this.seatsClient
      .getSalesPlanSeatsFromBlock(salesChannelId, eventSessionId, blockId, stampVersion, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanSeatsResponse>(),
        map((response) => response.seats!),
      );
  }

  getSalesExcludingMultiBlocksSeats(
    salesChannelId: number,
    eventSessionId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this.seatsClient
      .getSalesPlanSeats(salesChannelId, eventSessionId, null, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanSeatsResponse>(),
        map((response) => response.seats!),
      );
  }

  getDifferentialSalesExcludingMultiBlocksSeats(
    salesChannelId: number,
    eventSessionId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return this.seatsClient
      .getSalesPlanSeats(salesChannelId, eventSessionId, stampVersion, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetEventSessionSalesPlanSeatsResponse>(),
        map((response) => response.seats!),
      );
  }

  getSalesBlockViewLetters(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanLetters(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanLetterResponseDto[]>());
  }

  getSalesBlockViewContingentsImages(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionContingentImages(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentImageResponseDto[]>());
  }

  getSalesBlockViewImages(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanImageResponseDto> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanImages(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanImageResponseDto>());
  }

  getSalesBlockViewShapes(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanShapes(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanShapeResponseDto[]>());
  }

  getSalesExcludingMultiBlocksLetters(
    eventSessionId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanLetters(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanLetterResponseDto[]>());
  }

  getSalesExcludingMultiBlocksImages(eventSessionId: number): Observable<EventSessionPlanImageResponseDto> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanImages(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanImageResponseDto>());
  }

  getSalesExcludingMultiBlocksContingentsImages(
    eventSessionId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionContingentImages(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentImageResponseDto[]>());
  }

  getSalesExcludingMultiBlocksShapes(eventSessionId: number): Observable<EventSessionPlanShapeResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanShapes(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanShapeResponseDto[]>());
  }
}
